/**
 * iPodNode - Classic iPod music player node
 *
 * Features:
 * - Authentic classic iPod design based on real hardware
 * - Web Audio API for generating musical tones
 * - Click wheel navigation (menu, play/pause, next, prev)
 * - LCD screen with Now Playing, track info, progress bar
 *
 * Design:
 * - Iconic white iPod Classic look
 * - Green-tinted LCD screen
 * - Touch-responsive click wheel with segment dividers
 */
import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

export interface iPodNodeData {
  title?: string;
  [key: string]: unknown;
}

export type iPodNodeType = Node<iPodNodeData, 'ipod'>;

// ============================================================================
// AUDIO UTILITIES - Web Audio API tone generator
// ============================================================================

// Musical tracks with notes, timing info
const TRACKS = [
  { name: 'You Oughta Know', artist: 'Alanis Morissette', album: 'Jagged Little Pill', duration: 246, notes: [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 493.88, 523.25] },
  { name: 'Bitter Sweet Symphony', artist: 'The Verve', album: 'Urban Hymns', duration: 298, notes: [220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 392.00, 440.00] },
  { name: 'Wonderwall', artist: 'Oasis', album: 'Morning Glory', duration: 258, notes: [329.63, 392.00, 493.88, 392.00, 329.63, 261.63, 293.66, 329.63] },
  { name: 'Creep', artist: 'Radiohead', album: 'Pablo Honey', duration: 239, notes: [196.00, 220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 392.00] },
  { name: 'Smells Like Teen Spirit', artist: 'Nirvana', album: 'Nevermind', duration: 301, notes: [440.00, 493.88, 523.25, 587.33, 659.25, 698.46, 783.99, 880.00] },
];

// Create and play a tone using Web Audio API
function playTone(
  audioContext: AudioContext,
  frequency: number,
  duration: number = 0.3,
  type: OscillatorType = 'sine'
): void {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);

  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
  gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration);

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
}

// Format seconds to MM:SS
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// IPOD NODE COMPONENT
// ============================================================================

export const iPodNode = memo(({ id, data, selected }: NodeProps<iPodNodeType>) => {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [noteIndex, setNoteIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<number | null>(null);
  const timeIntervalRef = useRef<number | null>(null);

  // Initialize audio context
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    return audioContextRef.current;
  }, []);

  // Play notes in sequence
  const playSequence = useCallback(() => {
    const ctx = getAudioContext();
    const track = TRACKS[currentTrack];
    playTone(ctx, track.notes[noteIndex], 0.25, 'triangle');
    setNoteIndex((prev) => (prev + 1) % track.notes.length);
  }, [currentTrack, noteIndex, getAudioContext]);

  // Start/stop playback
  useEffect(() => {
    if (isPlaying && !showMenu) {
      playSequence();
      intervalRef.current = window.setInterval(playSequence, 300);

      // Time progress
      timeIntervalRef.current = window.setInterval(() => {
        setCurrentTime((prev) => {
          const track = TRACKS[currentTrack];
          if (prev >= track.duration) {
            // Auto-advance to next track
            setCurrentTrack((t) => (t + 1) % TRACKS.length);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [isPlaying, showMenu, playSequence, currentTrack]);

  // Reset on track change
  useEffect(() => {
    setNoteIndex(0);
    setCurrentTime(0);
  }, [currentTrack]);

  // Handlers
  const handlePlayPause = useCallback(() => {
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
    if (showMenu) {
      // Select menu item
      setShowMenu(false);
    } else {
      setIsPlaying((prev) => !prev);
    }
  }, [showMenu]);

  const handleNext = useCallback(() => {
    const ctx = getAudioContext();
    playTone(ctx, 1200, 0.03, 'square');
    if (showMenu) {
      setMenuIndex((prev) => Math.min(prev + 1, 3));
    } else {
      setCurrentTrack((prev) => (prev + 1) % TRACKS.length);
    }
  }, [showMenu, getAudioContext]);

  const handlePrev = useCallback(() => {
    const ctx = getAudioContext();
    playTone(ctx, 1200, 0.03, 'square');
    if (showMenu) {
      setMenuIndex((prev) => Math.max(prev - 1, 0));
    } else {
      setCurrentTrack((prev) => (prev - 1 + TRACKS.length) % TRACKS.length);
    }
  }, [showMenu, getAudioContext]);

  const handleMenu = useCallback(() => {
    const ctx = getAudioContext();
    playTone(ctx, 1000, 0.03, 'square');
    setShowMenu((prev) => !prev);
    setMenuIndex(0);
  }, [getAudioContext]);

  const track = TRACKS[currentTrack];
  const progress = (currentTime / track.duration) * 100;
  const remainingTime = track.duration - currentTime;

  return (
    <div className="relative">
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-2 !h-2 !bg-gray-400 !border !border-white !opacity-50"
        style={{ top: '50%' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-2 !h-2 !bg-gray-400 !border !border-white !opacity-50"
        style={{ top: '50%' }}
      />

      {/* iPod Body */}
      <div
        className={`
          relative overflow-hidden select-none
          transition-all duration-300
          ${selected ? 'ring-2 ring-blue-400 ring-offset-2' : ''}
        `}
        style={{
          width: 200,
          height: 350,
          background: 'linear-gradient(180deg, #fafafa 0%, #f0f0f0 30%, #e8e8e8 70%, #e0e0e0 100%)',
          borderRadius: 20,
          boxShadow: `
            0 12px 40px rgba(0,0,0,0.25),
            inset 0 1px 0 rgba(255,255,255,0.9),
            inset 0 -1px 0 rgba(0,0,0,0.1)
          `,
          border: '1px solid #c8c8c8',
        }}
      >
        {/* Screen Area */}
        <div
          style={{
            margin: '18px 18px 14px 18px',
            padding: 3,
            background: '#2a2a2a',
            borderRadius: 6,
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)',
          }}
        >
          {/* LCD Screen - Classic green tint */}
          <div
            style={{
              width: '100%',
              height: 115,
              background: 'linear-gradient(180deg, #9cb496 0%, #8aa882 50%, #7d9b75 100%)',
              borderRadius: 3,
              padding: '6px 8px',
              fontFamily: '"Chicago", "Helvetica Neue", sans-serif',
              fontSize: 11,
              color: '#1a1a1a',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {showMenu ? (
              // Menu View
              <>
                <div className="flex items-center justify-between border-b border-gray-600/30 pb-1 mb-1">
                  <span className="font-bold text-[10px]">iPod</span>
                  <BatteryIcon />
                </div>
                <div className="flex-1 space-y-0.5 text-[10px]">
                  {['Music', 'Photos', 'Videos', 'Settings'].map((item, i) => (
                    <div
                      key={item}
                      className={`px-1 py-0.5 rounded-sm ${
                        menuIndex === i ? 'bg-blue-700 text-white' : ''
                      }`}
                    >
                      {item} {menuIndex === i && '›'}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              // Now Playing View
              <>
                {/* Header */}
                <div className="flex items-center justify-between text-[9px] border-b border-gray-600/30 pb-1 mb-1">
                  <div className="flex items-center gap-1">
                    <span>{isPlaying ? '▶' : '❚❚'}</span>
                    <span className="font-bold">Now Playing</span>
                  </div>
                  <BatteryIcon />
                </div>

                {/* Track counter */}
                <div className="flex justify-between text-[8px] text-gray-700 mb-1">
                  <span>{currentTrack + 1} of {TRACKS.length}</span>
                  <span>♫</span>
                </div>

                {/* Track info */}
                <div className="flex-1 text-center space-y-0.5">
                  <div className="font-bold text-[11px] truncate">{track.name}</div>
                  <div className="text-[10px] text-gray-700 truncate">{track.artist}</div>
                  <div className="text-[9px] text-gray-600 truncate">{track.album}</div>
                </div>

                {/* Progress bar and time */}
                <div className="mt-auto">
                  <div className="w-full h-2 bg-gray-400/50 rounded-sm overflow-hidden mb-1">
                    <div
                      className="h-full bg-gray-700 transition-all duration-1000"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-gray-700">
                    <span>{formatTime(currentTime)}</span>
                    <span>-{formatTime(remainingTime)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Click Wheel */}
        <div
          className="absolute"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 22,
          }}
        >
          {/* Outer wheel with segments */}
          <div
            className="relative"
            style={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: 'linear-gradient(145deg, #f8f8f8, #e0e0e0)',
              boxShadow: `
                inset 0 2px 6px rgba(0,0,0,0.15),
                0 1px 1px rgba(255,255,255,0.9)
              `,
            }}
          >
            {/* Wheel segment dividers */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: `
                  linear-gradient(0deg, transparent 48%, #ccc 48%, #ccc 52%, transparent 52%),
                  linear-gradient(90deg, transparent 48%, #ccc 48%, #ccc 52%, transparent 52%)
                `,
              }}
            />

            {/* Menu button (top) */}
            <button
              type="button"
              onClick={handleMenu}
              className="nodrag nopan absolute text-[10px] text-gray-500 hover:text-gray-800 transition-colors"
              style={{
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                fontFamily: 'system-ui',
                letterSpacing: '0.5px',
              }}
            >
              menu
            </button>

            {/* Previous button (left) - ⏮ */}
            <button
              type="button"
              onClick={handlePrev}
              className="nodrag nopan absolute text-gray-500 hover:text-gray-800 transition-colors text-[11px]"
              style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }}
            >
              ⏮
            </button>

            {/* Next button (right) - ⏭ */}
            <button
              type="button"
              onClick={handleNext}
              className="nodrag nopan absolute text-gray-500 hover:text-gray-800 transition-colors text-[11px]"
              style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }}
            >
              ⏭
            </button>

            {/* Play/Pause button (bottom) - ▶❚❚ */}
            <button
              type="button"
              onClick={handlePlayPause}
              className="nodrag nopan absolute text-gray-500 hover:text-gray-800 transition-colors text-[11px]"
              style={{ bottom: 10, left: '50%', transform: 'translateX(-50%)' }}
            >
              ▶❚❚
            </button>

            {/* Center select button */}
            <button
              type="button"
              onClick={handlePlayPause}
              className="nodrag nopan absolute"
              style={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                background: 'linear-gradient(145deg, #ffffff, #ececec)',
                boxShadow: `
                  0 2px 4px rgba(0,0,0,0.15),
                  inset 0 1px 0 rgba(255,255,255,1),
                  0 0 0 1px rgba(0,0,0,0.05)
                `,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span className="sr-only">Select</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Battery icon component
function BatteryIcon() {
  return (
    <svg width="18" height="9" viewBox="0 0 18 9" fill="none">
      <rect x="0.5" y="0.5" width="15" height="8" rx="1.5" stroke="#1a1a1a" strokeWidth="1" />
      <rect x="16" y="2.5" width="2" height="4" rx="0.5" fill="#1a1a1a" />
      <rect x="2" y="2" width="12" height="5" rx="0.5" fill="#1a1a1a" />
    </svg>
  );
}

iPodNode.displayName = 'iPodNode';
